import React, { useState, useEffect } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import ChatContainer from './components/ChatContainer';
import DocumentSearch from './components/DocumentSearch';
import MetadataExtractor from './components/MetadataExtractor';
import ExportData from './components/ExportData';
import Settings from './components/Settings';
import './theme.css';
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://webapp-python-nlng-bots-api-cuasbrg3h5cyewgm.westeurope-01.azurewebsites.net";

const getParamsTagForMode = (mode = '') => {
  const m = (mode || '').toLowerCase();
  if (m === 'hse' || m.includes('hse')) return 'HSE_PARAMS';
  if (m === 'po' || m.includes('po')) return 'POS_PARAMS';
  if (m === 'spa' || m.includes('spa')) return 'CMM_PARAMS';
  if (m === 'csi' || m.includes('csi')) return 'CSI_PARAMS';
  return 'QMS_PARAMS';
};

const getChatEndpointForMode = (mode = '') => {
  const m = (mode || '').toLowerCase();
  if (m === 'hse' || m.includes('hse')) return '/v1/api/hse/chat/';
  if (m === 'po' || m.includes('po')) return '/v1/api/pos/chat/';
  if (m === 'spa' || m.includes('spa')) return '/v1/api/cmm/achat/';
  if (m === 'csi' || m.includes('csi')) return '/v1/api/csi/achat/';
  return '/v1/api/qms/achat/';
};

function App() {
  // ==================== UI State ====================
  const [currentView, setCurrentView] = useState('chat');
  const [selectedChatMode, setSelectedChatMode] = useState(() => {
    try {
      const saved = localStorage.getItem('chatbot_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.defaultAssistant) return parsed.defaultAssistant;
      }
    } catch (_) {}
    return 'QMS';
  });
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedRecentChat, setSelectedRecentChat] = useState(null);
  const [recentChats, setRecentChats] = useState([]);
  
  // ==================== Theme State ====================
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  // ==================== Settings State ====================
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('chatbot_settings');
    return saved ? JSON.parse(saved) : {
      enableSuggestions: true,
      typingIndicators: true,
      theme: 'light',
      chatBubbleStyle: 'modern',
      fontSize: 16,
      soundNotifications: true,
      notifyNewMessage: true,
      emailSummary: 'none',
      autoLogout: false,
      hideSensitiveData: false,
      defaultAssistant: 'QMS'
    };
  });

  // ==================== Search History State ====================
  const [searchHistory, setSearchHistory] = useState([
    { id: 1, title: "Health care policy" },
    { id: 2, title: "Safety protocols 2024" }
  ]);

  // ==================== State for passing search term ====================
  const [initialSearchTerm, setInitialSearchTerm] = useState('');

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Inject feedback widget with SSO user data
  useEffect(() => {
    const rawDisplayName = localStorage.getItem('displayName') || '';
    const userId = localStorage.getItem('userId') || '';
    const namePart = rawDisplayName.split('(')[0].trim();
    let fullName = namePart;
    if (namePart.includes(',')) {
      const [last, first] = namePart.split(',').map(s => s.trim());
      fullName = `${first} ${last}`;
    }
    const emailName = fullName.toLowerCase().replace(/\s+/g, '.');
    const email = emailName ? `${emailName}@nlng.com` : '';

    const script = document.createElement('script');
    script.src = 'https://ashy-river-0753f3f03.7.azurestaticapps.net/feedback-widget.js';
    script.async = true;
    script.dataset.fpAppKey = '9df1fcd8038e3d76e436d3f84c9dc491';
    script.dataset.fpApiBaseUrl = 'https://feedback-portal-api-dev-04-esa8aud3cxe8hqgw.westeurope-01.azurewebsites.net';
    script.dataset.fpUserEmail = email;
    script.dataset.fpUserName = rawDisplayName;
    script.dataset.fpUserAccount = userId;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem('chatbot_settings', JSON.stringify(settings));
  }, [settings]);

  // Update selected chat mode when settings change
  useEffect(() => {
    if (selectedChatMode !== settings.defaultAssistant) {
      setSettings(prev => ({ ...prev, defaultAssistant: selectedChatMode }));
    }
  }, [selectedChatMode, settings.defaultAssistant]);

  // Fetch recent chats from backend when chat mode changes
  useEffect(() => {
    const fetchRecentChats = async () => {
      const user_id = localStorage.getItem('userId') || '1';
      const params_tag = getParamsTagForMode(selectedChatMode);

      try {
        const query = new URLSearchParams({ user_id, params_tag, retrieved_len: 10 }).toString();
        const res = await fetch(`${API_BASE_URL}/v1/api/c/get-previous-chat?${query}`);
        const data = await res.json();
        if (data && data.conversation_names && data.conversation_ids) {
          console.log('[fetchRecentChats] raw response:', data);
          const lct = data.last_conversation_time;
          const chats = data.conversation_names.map((name, idx) => {
            const convId = data.conversation_ids[idx];
            // last_conversation_time may be an array OR an object keyed by conversation ID
            let ts = null;
            if (lct != null) {
              if (Array.isArray(lct)) {
                ts = lct[idx] != null ? lct[idx] : null;
              } else if (typeof lct === 'object') {
                ts = lct[convId] != null ? lct[convId] : (lct[String(convId)] != null ? lct[String(convId)] : null);
              } else {
                // scalar — same time for all chats (unlikely but safe)
                ts = lct;
              }
            }
            if (ts == null && data.timestamps) ts = data.timestamps[idx] ?? null;
            return {
              id: convId,
              title: name,
              lastMessage: (data.last_messages && data.last_messages[idx]) ? data.last_messages[idx] : 'Hello! How can I assist you today?',
              timestamp: ts
            };
          });
          setRecentChats(chats);
        } else {
          setRecentChats([]);
        }
      } catch (e) {
        setRecentChats([]);
      }
    };

    fetchRecentChats();
  }, [selectedChatMode]);

  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
  };

  // Handle individual setting changes; key '__RESET__' resets to defaults
  const handleSettingsChange = (key, value) => {
    if (key === '__RESET__') {
      const defaults = {
        enableSuggestions: true,
        typingIndicators: true,
        theme: 'light',
        chatBubbleStyle: 'modern',
        fontSize: 16,
        soundNotifications: true,
        notifyNewMessage: true,
        notifications: true,
        emailNotifications: true,
        twoFactorAuth: false,
        endToEndEncryption: true,
        autoLogout: false,
        hideSensitiveData: false,
        defaultAssistant: 'QMS'
      };
      setSettings(defaults);
      toggleTheme('light');
    } else {
      setSettings(prev => ({ ...prev, [key]: value }));
    }
  };

  // Apply font size from settings to CSS variable
  useEffect(() => {
    if (settings?.fontSize) {
      document.documentElement.style.setProperty('--chat-font-size', `${settings.fontSize}px`);
    }
  }, [settings?.fontSize]);

  // Open a recent chat (fetch full conversation from backend when possible)
  const openChat = async (chat) => {
    if (!chat || !chat.id) {
      // Clear the current chat first so ChatContainer sees null → resets to welcome screen
      setSelectedRecentChat(null);
      await startNewChat();
      return;
    }
    setCurrentView('chat');
    // optimistic set so UI navigates immediately
    setSelectedRecentChat({ id: chat.id, title: chat.title, content: chat.lastMessage || '' });

    // attempt to load full conversation from backend
    try {
      const params_tag = getParamsTagForMode(selectedChatMode);
      const user_id = localStorage.getItem('userId') || '1';
      const res = await fetch(`${API_BASE_URL}/v1/api/c/get-chat-data?user_id=${user_id}&params_tag=${params_tag}&conversation_id=${chat.id}`);
      if (!res.ok) return; // keep optimistic state
      const data = await res.json();
      console.log('[openChat] get-chat-data response:', data);
      // Support multiple possible response shapes from the backend
      const rawList = Array.isArray(data.conversations) ? data.conversations
        : Array.isArray(data.chat_history) ? data.chat_history
        : Array.isArray(data.messages) ? data.messages
        : Array.isArray(data.data) ? data.data
        : null;
      const restoredMessages = rawList
        ? rawList.map((item) => ({
            is_user: item.is_user ?? item.isUser ?? item.role === 'user',
            text: item.text ?? item.content ?? item.message ?? '',
            timestamp: item.timestamp || item.time || item.created_at || null,
            message_id: item.message_id || null
          }))
        : null;
      if (restoredMessages && restoredMessages.length > 0) {
        setSelectedRecentChat({
          id: chat.id,
          title: chat.title,
          fullConversation: restoredMessages,
          content: restoredMessages[restoredMessages.length - 1].text || chat.lastMessage || ''
        });
      }
    } catch (e) {
      // keep optimistic state on failure
      console.warn('Failed to load full conversation', e);
    }
  };

  const handleSelectRecentChat = (chat) => {
    setShowSettings(false); // close Settings if open so user sees the chat
    openChat(chat);
  };

  // Function to add a search to history
  const addToSearchHistory = (searchTerm) => {
    if (!searchTerm.trim()) return;
    
    const exists = searchHistory.some(item => item.title.toLowerCase() === searchTerm.toLowerCase());
    
    if (!exists) {
      const newSearch = {
        id: Date.now(),
        title: searchTerm
      };
      
      setSearchHistory(prev => [newSearch, ...prev].slice(0, 10));
    }
  };

  // Function to add an extraction to history
  const addToExtractionHistory = (extractionTitle) => {
    if (!extractionTitle.trim()) return;
    
    // Just a placeholder - extractions would be handled by MetadataExtractor component
    console.log('Extraction added:', extractionTitle);
  };

  const sendMessageToBackend = async ({ conversationId, message, chatMode }) => {
    // returns { bot_response } or string
    if (!conversationId || !message) return null;
    try {
      const user_id = localStorage.getItem('userId') || '1';
      const endpoint = getChatEndpointForMode(chatMode);

      const res = await fetch(`${API_BASE_URL}${endpoint}${conversationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, user_query: message })
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.bot_response || data.response || null;
    } catch (e) {
      console.warn('sendMessageToBackend failed', e);
      return null;
    }
  };

  const startNewChat = async (overrideMode) => {
    // create server conversation and return an object usable by ChatContainer
    // overrideMode lets callers pass the new mode before selectedChatMode state has updated
    const modeToUse = overrideMode || selectedChatMode;
    try {
      const user_id = localStorage.getItem('userId') || '1';
      const params_tag = getParamsTagForMode(modeToUse);

      const res = await fetch(`${API_BASE_URL}/v1/api/c/new-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, params_tag })
      });
      const data = await res.json().catch(() => ({}));
      const conversation_id = data.conversation_id || `local-${Date.now()}`;
      const timestamp = new Date().toISOString();
      const uniqueTitle = `Chat ${new Date().toLocaleString()}`;

      const newChat = {
        id: conversation_id,
        title: uniqueTitle,
        lastMessage: 'Hello! How can I assist you today?',
        timestamp
      };

      setRecentChats(prev => [newChat, ...prev]);
      setSelectedRecentChat({ id: conversation_id, title: uniqueTitle, content: `Hello! I am your ${modeToUse} virtual assistant. How can I assist you today?` });
      setCurrentView('chat');
      return newChat;
    } catch (e) {
      console.error('startNewChat failed', e);
      const fallback = { id: `local-${Date.now()}`, title: `Chat ${Date.now()}`, lastMessage: 'Hello! How can I assist you today?', timestamp: new Date().toISOString() };
      setRecentChats(prev => [fallback, ...prev]);
      setSelectedRecentChat({ id: fallback.id, title: fallback.title, content: 'Hello! How can I assist you today?' });
      return fallback;
    }
  };

  // Called when user picks a different assistant from the top dropdown.
  // Switches mode AND immediately creates a fresh backend conversation so
  // there is always a valid conversation_id before the user types anything.
  const handleChangeChatMode = async (newMode) => {
    setSelectedChatMode(newMode);
    setSelectedRecentChat(null); // clear any stale conversation immediately
    await startNewChat(newMode); // pass newMode explicitly — state hasn't updated yet
  };

  // Update a chat's title in recentChats (called by ChatContainer after first reply)
  const handleUpdateChatTitle = (conversationId, newTitle) => {
    setRecentChats(prev =>
      prev.map(c => c.id === conversationId ? { ...c, title: newTitle } : c)
    );
    // Also sync selectedRecentChat so the header stays current
    setSelectedRecentChat(prev =>
      prev && prev.id === conversationId ? { ...prev, title: newTitle } : prev
    );
  };

  // Delete a chat from the sidebar and optionally from the backend
  const handleDeleteChat = async (chatId) => {
    setRecentChats(prev => prev.filter(c => c.id !== chatId));
    if (selectedRecentChat?.id === chatId) {
      setSelectedRecentChat(null);
    }
    try {
      const user_id = localStorage.getItem('userId') || '1';
      const params_tag = getParamsTagForMode(selectedChatMode);
      await fetch(`${API_BASE_URL}/v1/api/c/delete-chat`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, params_tag, conversation_id: chatId })
      });
    } catch (e) {
      console.warn('Delete chat API failed (removed from UI anyway):', e);
    }
  };

  const renderMainContent = () => {
    if (showSettings) {
      return (
        <Settings 
          onClose={() => setShowSettings(false)} 
          theme={theme}
          onThemeChange={toggleTheme}
          settings={settings}
          onSettingsChange={handleSettingsChange}
          selectedChatMode={selectedChatMode}
          onChangeChatMode={setSelectedChatMode}
        />
      );
    }

    switch (currentView) {
      case 'documentSearch':
        return (
          <DocumentSearch 
            onBack={() => {
              setCurrentView('chat');
              setInitialSearchTerm('');
            }} 
            onNavigate={setCurrentView}
            onSearch={addToSearchHistory}
            initialSearchTerm={initialSearchTerm}
          />
        );
      case 'metadataExtractor':
        return (
          <MetadataExtractor 
            onBack={() => setCurrentView('chat')} 
            onNavigate={setCurrentView}
            onExtraction={addToExtractionHistory}
          />
        );
      case 'exportData':
        return (
          <ExportData 
            onBack={() => setCurrentView('chat')} 
            onNavigate={setCurrentView}
          />
        );
      case 'chat':
      default:
        return (
          <ChatContainer 
            chatMode={selectedChatMode}
            onChangeChatMode={handleChangeChatMode}
            onNavigate={setCurrentView}
            sidebarCollapsed={sidebarCollapsed}
            onSettingsClick={() => setShowSettings(true)}
            environment="chat"
            selectedRecentChat={selectedRecentChat}
            recentChats={recentChats}
            onStartChat={startNewChat}
            onSendMessage={sendMessageToBackend}
            onOpenChat={openChat}
            onUpdateChatTitle={handleUpdateChatTitle}
            onDepartmentChange={(dept) => console.log("Department selected:", dept)}
            settings={settings}
            onThemeChange={toggleTheme}
          />
        );
    }
  };

  return (
    <div className="app" data-theme={theme}>
      <Sidebar 
        onNavigate={setCurrentView}
        currentView={currentView}
        onSettingsClick={() => setShowSettings(true)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onSelectRecentChat={handleSelectRecentChat}
        currentChatMode={selectedChatMode}
        recentChats={recentChats}
        selectedRecentChat={selectedRecentChat}
        onDeleteChat={handleDeleteChat}
        displayName={localStorage.getItem('displayName') || ""}
        />
      {renderMainContent()}
    </div>
  );
}

export default App;